import { Button, Card, Description, Label, Radio, RadioGroup } from '@heroui/react';
import type { RecurrenceFrequency } from '../../../api/my-parking-spots';
import { FormSwitch } from '../../../components/ui/form-switch';
import { formatDateTimeBR } from '../../../utils/date-utils';
import {
  buildRecurrenceOccurrenceIsos,
  DAILY_OCCURRENCE_OPTIONS,
  WEEKLY_OCCURRENCE_OPTIONS,
} from '../utils/recurrence';

interface RecurrenceSectionProps {
  enabled: boolean;
  frequency: RecurrenceFrequency;
  occurrences: number;
  startAtIso: string | null;
  endAtIso: string | null;
  overlapError?: string | null;
  onEnabledChange: (enabled: boolean) => void;
  onFrequencyChange: (frequency: RecurrenceFrequency) => void;
  onOccurrencesChange: (occurrences: number) => void;
}

export function RecurrenceSection({
  enabled,
  frequency,
  occurrences,
  startAtIso,
  endAtIso,
  overlapError,
  onEnabledChange,
  onFrequencyChange,
  onOccurrencesChange,
}: RecurrenceSectionProps) {
  const options = frequency === 'DAILY' ? DAILY_OCCURRENCE_OPTIONS : WEEKLY_OCCURRENCE_OPTIONS;
  const preview =
    enabled && startAtIso && endAtIso
      ? buildRecurrenceOccurrenceIsos(startAtIso, endAtIso, frequency, occurrences)
      : [];

  return (
    <Card variant="secondary">
      <Card.Header className="border-b border-separator pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Card.Title className="text-base font-semibold">Repetir janela</Card.Title>
            <Card.Description>
              Repete o mesmo intervalo (ex.: sexta 18h até domingo 20h) nas próximas semanas.
            </Card.Description>
          </div>
          <FormSwitch
            size="sm"
            label="Repetir"
            isSelected={enabled}
            onChange={onEnabledChange}
          />
        </div>
      </Card.Header>
      {enabled ? (
        <Card.Content className="flex flex-col gap-4 pt-4">
          <RadioGroup
            variant="secondary"
            orientation="horizontal"
            className="gap-4 [&_[data-slot=radio]]:mt-0"
            aria-label="Frequência da repetição"
            value={frequency}
            onChange={(next) => onFrequencyChange(next as RecurrenceFrequency)}
          >
            <Radio value="WEEKLY">
              <Radio.Content>
                <Radio.Control>
                  <Radio.Indicator />
                </Radio.Control>
                Toda semana
              </Radio.Content>
            </Radio>
            <Radio value="DAILY">
              <Radio.Content>
                <Radio.Control>
                  <Radio.Indicator />
                </Radio.Control>
                Todos os dias
              </Radio.Content>
            </Radio>
          </RadioGroup>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              {frequency === 'DAILY' ? 'Por quantos dias' : 'Por quantas semanas'}
            </Label>
            <div className="flex flex-wrap gap-2">
              {options.map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={occurrences === option ? 'primary' : 'secondary'}
                  onPress={() => onOccurrencesChange(option)}
                >
                  {option}
                  {frequency === 'DAILY' ? ' dias' : ' semanas'}
                </Button>
              ))}
            </div>
          </div>

          {overlapError ? (
            <Description className="text-danger">{overlapError}</Description>
          ) : preview.length > 0 ? (
            <Description>
              {preview.length} janelas:{' '}
              {preview
                .slice(0, 4)
                .map((item) => formatDateTimeBR(item.startAt))
                .join(' · ')}
              {preview.length > 4 ? '…' : ''}
            </Description>
          ) : null}
        </Card.Content>
      ) : null}
    </Card>
  );
}
